import { z } from 'zod'
import { channelSourceData } from './channelSource'
import { achDetailsSchema } from './billing/billing'
import { FormattedAddressData } from './ips/address'
import { pspResponseData } from './billing/psp'
import { offerAttributesSchema } from './ips/createContract'
import { AchCodes } from './initCheckout'

export const personSchema = z.object({
    personId: z.string().min(1),
})

export const sdkBillingAddressSchema = z
    .object({
        stateCode: z
            .string()
            .min(2)
            .describe(
                'This is the 2 letter state code in which the customer lives. See https://developer.paysafe.com/en/support/reference-information/codes/#state-codes',
            ),
    })
    .and(
        FormattedAddressData.pick({
            number: true,
            street: true,
            subBuilding: true,
            postalCode: true,
            city: true,
            countryCode: true,
        }).required({
            city: true,
            postalCode: true,
            countryCode: true,
            street: true,
        }),
    )

export const offerReference = z.string().describe('Offer reference')
export const offerId = z
    .number()
    .describe(
        'Offer ID, this is an optional field as the upstream will always have access to the offer reference. There is a 1 to 1 mapping between an offer id and offer reference.',
    )

export const offerBaseSchema = z.object({
    offerId: offerId.optional(),
    offerReference,
})

export const offerPricingKey = z
    .number()
    .describe('Key to offer pricing option')

export const ContractAttributesSchema = offerAttributesSchema
    .pick({
        productVariantId: true,
        netTotalAmount: true,
        taxTotalAmount: true,
        netFirstPaymentAmount: true,
        taxRate: true,
        periodOfCover: true,
        levelOfCoverCode: true,
        effectiveDate: true,
        thirdPartyContractExpiryDate: true,
    })
    .extend({
        offerPricingKey,
    })

export type ContractAttributes = z.infer<typeof ContractAttributesSchema>

// Discount Profile Attributes Schema
export const discountProfileAttributes = z.object({
    netAmountBeforeDiscount: z
        .number()
        .describe('Total amount before discount without tax applied'),
    discountAmount: z
        .number()
        .describe(
            'The amount of the discount applied, depending on the discount type, e.g. is discountType is P, then this discount amount is percent.',
        ),
    discountTermInMonths: z
        .number()
        .describe('The number of months the discount is applied'),
})

// Item Schema
export const itemSchema = z.object({
    itemId: z.string().describe('Item ID'),
})

export type ItemType = z.infer<typeof itemSchema>

export const contractAttribute = z.object({
    contractsAttributes: ContractAttributesSchema,
    discountProfileAttributes: discountProfileAttributes.optional(),
    itemDetails: itemSchema,
})

export const directOfferWithContractsOverrideSchema = z.object({
    contracts: z.array(contractAttribute),
})

export const billingDetailsSchema = z.discriminatedUnion('type', [
    achDetailsSchema.extend({
        type: z.literal('ach'),
    }),
    z
        .object({
            type: z.literal('card'),
        })
        .merge(
            pspResponseData.pick({
                pspToken: true,
            }),
        ),
])

/**
 * Note: Do NOT validate against this object directly, use CreateContractSyncSDKSchema instead
 * This is only for the shape of the object so that we can extend for the async sdk lambda
 */
export const CreateContractSyncSDKObject = z.object({
    transactionId: z.string().uuid().optional(),
    channelSourceData: channelSourceData.omit({ userId: true }),
    person: personSchema,
    billing: z.object({
        billingDetails: billingDetailsSchema,
        address: sdkBillingAddressSchema,
    }),
    achDiscountCode: z
        .nativeEnum(AchCodes)
        .describe(
            'ACH discount code. The discount code that was applied to the contract. Must be present when any contract has discountProfileAttributes',
        )
        .optional(),
    offers: z
        .array(offerBaseSchema.and(directOfferWithContractsOverrideSchema))
        .min(1),
})

const createContractRefine = (
    data: z.infer<typeof CreateContractSyncSDKObject>,
    ctx: z.RefinementCtx,
): void => {
    const hasDiscountProfile = data.offers.some((offer) =>
        offer.contracts.some((contract) => contract.discountProfileAttributes),
    )
    if (hasDiscountProfile && !data.achDiscountCode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
                'achDiscountCode must be present when any contract has discountProfileAttributes',
            path: ['achDiscountCode'],
        })
    }
}

// Use this schema for object validation as this includes refining validation
export const CreateContractSyncSDKSchema =
    CreateContractSyncSDKObject.superRefine(createContractRefine)

export type CreateContractSyncSDKRequestSchema = z.infer<
    typeof CreateContractSyncSDKSchema
>

// Create Contract Sync Response Schema
export const CreateContractSDKSyncResponseSchema = z
    .object({
        transactionId: z.string(),
        message: z.string().optional(),
        existingItems: z
            .array(
                z.object({
                    offerReference,
                    offerId: offerId.optional(),
                    offerState: z
                        .string()
                        .startsWith('attempt_')
                        .or(z.string().startsWith('contract_')),
                    transactionId: z.string(),
                    inflight: z
                        .boolean()
                        .optional()
                        .describe(
                            "Is the offer inflight (In Progress). This is an optional field as this inflight status does not exist on individual contracts where there is a contract id. Will only exist where the offerState begins with 'attempt_' string prefix",
                        ),
                    personId: z.string().optional(),
                    contractId: z.string().optional(),
                }),
            )
            .optional(),
    })
    .openapi('CreateContractSDKSyncResponse', {
        example: { transactionId: '123456' },
    })
    .describe(
        'Response for create contract SDK sync endpoint, transactionId is returned for async processing and can be used later on in the process to link a request to a contract creation. Will return existingItems if we have already processed this offer or the offer is inflight (current in progress)',
    )

export type CreateContractSyncSDK = z.infer<typeof CreateContractSyncSDKObject>
export type BillingDetails = z.infer<typeof billingDetailsSchema>
export type CreateContractSDKSyncResponseType = z.infer<
    typeof CreateContractSDKSyncResponseSchema
>
